/**
 * Pure reducer: `(GameState, Action) → GameState`.
 *
 * Server-authoritative; any transient client state (animation frames) lives
 * in scenes. Must stay side-effect-free so the reducer remains unit-testable
 * in node/jsdom.
 *
 * @packageDocumentation
 */

import type { OutMsg, Round } from '@sg/shared';
import type { GameState, PollState, SceneName } from './types.js';

/** Union of dispatch actions. `connection` tracks WS status; `ws` replays a server frame. */
export type Action =
  | { type: 'connection'; value: GameState['connection'] }
  | { type: 'ws'; msg: OutMsg };

/**
 * Pure reducer. Must be side-effect-free. Returns the same reference when
 * nothing changes (subscribers skip no-ops).
 */
export function reduce(state: GameState, action: Action): GameState {
  if (action.type === 'connection') return applyConnection(state, action.value);
  return reduceWs(state, action.msg);
}

function applyConnection(state: GameState, value: GameState['connection']): GameState {
  let scene: SceneName = state.scene;
  if (value === 'open' && state.scene === 'connecting') scene = 'lobby';
  else if (value !== 'open' && state.scene === 'lobby') scene = 'connecting';
  return { ...state, connection: value, scene };
}

function reduceWs(state: GameState, msg: OutMsg): GameState {
  switch (msg.type) {
    case 'state_snapshot':
      return applySnapshot(state, msg.state);
    case 'poll_open':
      return applyPollOpen(state, msg);
    case 'poll_tally':
      return applyPollTally(state, msg);
    case 'poll_tally_update':
      return applyPollTallyUpdate(state, msg);
    case 'poll_close':
      return applyPollClose(state, msg);
    case 'clue_arm':
      return applyClueArm(state, msg);
    case 'clue_reveal':
      return applyClueReveal(state, msg);
    case 'grace_begin':
      return applyGraceBegin(state, msg);
    case 'answer_reveal':
      return applyAnswerReveal(state, msg);
    case 'score_update':
      return applyScoreUpdate(state, msg);
    case 'round_change':
      return applyRoundChange(state, msg);
    case 'calibration_prompt':
      return applyCalibrationPrompt(state, msg);
    case 'calibration_result':
      return applyCalibrationResult(state, msg);
    case 'tick':
      return { ...state, lastServerTs: msg.server_ts };
    case 'pong':
      return state;
    default: {
      const _exhaustive: never = msg;
      void _exhaustive;
      return state;
    }
  }
}

type SnapshotShape = Extract<OutMsg, { type: 'state_snapshot' }>['state'];
type SnapshotPoll = NonNullable<SnapshotShape['current_poll']>;

function pollFromSnapshot(sp: SnapshotPoll): PollState {
  const out: PollState = Object.create(null) as PollState;
  out.phase = sp.phase;
  out.options = sp.options;
  out.endsAtMs = sp.ends_at_ms;
  out.tallies = sp.tallies;
  out.winner = null;
  return out;
}

function applySnapshot(state: GameState, snap: SnapshotShape): GameState {
  const poll = snap.current_poll ? pollFromSnapshot(snap.current_poll) : null;
  const activeClue = snap.current_clue
    ? (state.activeClue ?? {
        clue: snap.current_clue,
        revealAtMs: snap.server_ts,
        hardEndMs: snap.server_ts + 10_000,
        graceEndMs: snap.server_ts + 10_000 + snap.broadcast_delay_seconds * 1000,
      })
    : null;
  // Snapshot may carry a settings.camera_position; fall back to current state
  // (which is seeded from the URL override at boot).
  const cameraPosition = snap.settings?.camera_position ?? state.cameraPosition;
  return {
    ...state,
    tenantId: snap.tenant_id,
    round: snap.round,
    broadcastDelaySeconds: snap.broadcast_delay_seconds,
    cameraPosition,
    lastServerTs: snap.server_ts,
    leaderboard: snap.leaderboard_top10,
    leaderboardAllTime: snap.leaderboard_all_time ?? state.leaderboardAllTime,
    poll,
    scene: snapshotStateToScene(snap.state, state.scene),
    activeClue,
  };
}

function applyPollOpen(state: GameState, msg: Extract<OutMsg, { type: 'poll_open' }>): GameState {
  const p: PollState = {
    phase: msg.phase,
    options: msg.options,
    endsAtMs: msg.ends_at_ms,
    tallies: {},
    winner: null,
  };
  return { ...state, poll: p, scene: msg.phase === 'category' ? 'poll_category' : 'poll_clue_value' };
}

function applyPollTally(state: GameState, msg: Extract<OutMsg, { type: 'poll_tally' }>): GameState {
  if (!state.poll || state.poll.phase !== msg.phase) return state;
  return { ...state, poll: { ...state.poll, tallies: msg.tallies } };
}

function applyPollTallyUpdate(
  state: GameState,
  msg: Extract<OutMsg, { type: 'poll_tally_update' }>,
): GameState {
  // v1.3 live tally frame. Merge into the active poll regardless of phase —
  // the message has no phase field; backend only emits one at a time per poll.
  if (!state.poll) return state;
  return { ...state, poll: { ...state.poll, tallies: msg.counts } };
}

function applyPollClose(state: GameState, msg: Extract<OutMsg, { type: 'poll_close' }>): GameState {
  if (!state.poll) return state;
  return { ...state, poll: { ...state.poll, winner: msg.winner } };
}

function applyClueArm(state: GameState, msg: Extract<OutMsg, { type: 'clue_arm' }>): GameState {
  const armed = { preview: msg.cluePreview, revealAtMs: msg.reveal_at_ms };
  return { ...state, armedClue: armed, poll: null, scene: 'board' };
}

function applyClueReveal(state: GameState, msg: Extract<OutMsg, { type: 'clue_reveal' }>): GameState {
  const active = {
    clue: msg.clue,
    revealAtMs: msg.reveal_at_ms,
    hardEndMs: msg.hard_end_ms,
    graceEndMs: msg.grace_end_ms,
  };
  return { ...state, armedClue: null, activeClue: active, scene: 'clue_zoom' };
}

function applyGraceBegin(state: GameState, msg: Extract<OutMsg, { type: 'grace_begin' }>): GameState {
  if (!state.activeClue) return state;
  return {
    ...state,
    activeClue: { ...state.activeClue, graceEndMs: msg.grace_end_ms },
    scene: 'grace',
  };
}

function applyAnswerReveal(state: GameState, msg: Extract<OutMsg, { type: 'answer_reveal' }>): GameState {
  const ar = { displayAnswer: msg.display_answer, topScorers: msg.top_scorers };
  return { ...state, answerReveal: ar, scene: 'answer_reveal' };
}

function applyScoreUpdate(state: GameState, msg: Extract<OutMsg, { type: 'score_update' }>): GameState {
  const su = { deltas: msg.deltas, leaderboard: msg.leaderboard_top10 };
  return {
    ...state,
    scoreUpdate: su,
    leaderboard: msg.leaderboard_top10,
    activeClue: null,
    answerReveal: null,
    scene: 'board',
  };
}

function sceneForRound(r: Round): SceneName {
  if (r === 'results') return 'podium';
  return 'round_transition';
}

function applyRoundChange(state: GameState, msg: Extract<OutMsg, { type: 'round_change' }>): GameState {
  return { ...state, round: msg.round, scene: sceneForRound(msg.round) };
}

function applyCalibrationPrompt(
  state: GameState,
  msg: Extract<OutMsg, { type: 'calibration_prompt' }>,
): GameState {
  const cal = { revealSentTs: msg.reveal_sent_ts, measuredDelayMs: null, samples: 0 };
  return { ...state, calibration: cal, scene: 'calibration' };
}

function applyCalibrationResult(
  state: GameState,
  msg: Extract<OutMsg, { type: 'calibration_result' }>,
): GameState {
  const base = state.calibration ?? { revealSentTs: 0, measuredDelayMs: null, samples: 0 };
  const merged = { ...base, measuredDelayMs: msg.measured_delay_ms, samples: msg.samples };
  return { ...state, calibration: merged };
}

function snapshotStateToScene(serverState: string, fallback: SceneName): SceneName {
  const norm = serverState.toUpperCase();
  if (norm === 'LOBBY') return 'lobby';
  if (norm === 'CATEGORY_POLL') return 'poll_category';
  if (norm === 'CLUE_SELECT_POLL') return 'poll_clue_value';
  if (norm === 'CLUE_ARMED') return 'board';
  if (norm === 'CLUE_REVEALED') return 'clue_zoom';
  if (norm === 'GRACE_WINDOW') return 'grace';
  if (norm === 'ANSWER_REVEAL') return 'answer_reveal';
  if (norm === 'SCORE_UPDATE') return 'board';
  if (norm.startsWith('ROUND_')) return 'round_transition';
  if (norm === 'RESULTS') return 'podium';
  if (norm === 'CALIBRATION') return 'calibration';
  // Legacy round-2 / final-round states (INTERMISSION, FINAL_SETUP,
  // FR_PROMPT_REVEAL*, FINAL_RESULTS) were removed Apr 2026. Backend no
  // longer emits them; an old snapshot mid-deploy falls through to fallback.
  return fallback;
}
